import {
  DataTag,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'

import {
  BodyType,
  customInstance,
  ErrorType,
} from '@/features/auth/axios-config'
import {
  ErrorResponse,
  RequestAssignPermission,
  RequestCreateInvitation,
  RequestUpdateUser,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  DEFAULT_STALE_TIME_MS,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import {
  DEFAULT_SORT_BY,
  ERoleType,
  getRoleDisplayLabel,
  QUERY_KEY_USER_DETAIL,
  QUERY_KEY_USER_LIST,
  USER_DETAIL_STALE_TIME,
  USER_LIST_QUERY_KEY,
} from '../constants'
import {
  createInvitation,
  getUserById,
  SecondParameter,
  updateUserById,
  updateUserPermissions,
  userManagementApi,
} from '../services'
import { UserManagementFilters } from '../types'

export * from './useGetRoles'

export const USER_ROLE_OPTIONS = [
  {
    label: getRoleDisplayLabel(ERoleType.ORGANIZATION_ADMIN),
    value: ERoleType.ORGANIZATION_ADMIN,
  },
  {
    label: getRoleDisplayLabel(ERoleType.PROJECT_LEAD),
    value: ERoleType.PROJECT_LEAD,
  },
  {
    label: getRoleDisplayLabel(ERoleType.TEAM_MEMBER),
    value: ERoleType.TEAM_MEMBER,
  },
  {
    label: getRoleDisplayLabel(ERoleType.REVIEWER),
    value: ERoleType.REVIEWER,
  },
  {
    label: getRoleDisplayLabel(ERoleType.VIEWER),
    value: ERoleType.VIEWER,
  },
]

export const useUserManagementList = (params: {
  search?: string
  filters?: UserManagementFilters
  page?: number
  rowsPerPage?: number
  sort?: { column: string | null; direction: SortOrder }
  enabled?: boolean
}) => {
  const {
    search = '',
    filters = {},
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    enabled = true,
  } = params

  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    DEFAULT_SORT_BY
  )

  return useQuery({
    queryKey: USER_LIST_QUERY_KEY([search, filters, page, rowsPerPage, sortBy]),
    queryFn: async () => {
      const filterBy: Record<string, string[]> = {}

      if (filters.role_id && filters.role_id.length > 0) {
        filterBy.role_id = filters.role_id
      }
      if (filters.status && filters.status.length > 0) {
        filterBy.status = filters.status
      }

      const { data, pagination } = await userManagementApi.listUsers({
        q: search,
        filter_by: filterBy,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
      })
      return { data, pagination }
    },
    staleTime: DEFAULT_STALE_TIME_MS,
    enabled,
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => userManagementApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_LIST],
        refetchType: 'active',
      })
    },
  })
}

/**
 * @summary Create a user invitation via Ascend ID
 */
export const useCreateInvitation = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createInvitation>>,
      TError,
      { data: BodyType<RequestCreateInvitation> },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createInvitation>>,
  TError,
  { data: BodyType<RequestCreateInvitation> },
  TContext
> => {
  const mutationKey = ['useCreateInvitation']

  const hasMutationKey =
    options?.mutation &&
    'mutationKey' in options.mutation &&
    options.mutation.mutationKey

  let resolvedOptions
  if (!options) {
    resolvedOptions = { mutation: { mutationKey }, request: undefined }
  } else if (hasMutationKey) {
    resolvedOptions = options
  } else {
    resolvedOptions = {
      ...options,
      mutation: { ...options.mutation, mutationKey },
    }
  }

  const { mutation: mutationOptions, request: requestOptions } = resolvedOptions

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createInvitation>>,
    { data: BodyType<RequestCreateInvitation> }
  > = (props) => {
    const { data } = props ?? {}

    return createInvitation(data, requestOptions)
  }

  return useMutation({ mutationFn, ...mutationOptions }, queryClient)
}

export const getUserByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserById>>,
  TError = ErrorType<ErrorResponse>,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>
    >
    request?: SecondParameter<typeof customInstance>
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? [QUERY_KEY_USER_DETAIL, userId]

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserById>>> = ({
    signal,
  }) => getUserById(userId, requestOptions, signal)

  return {
    queryKey,
    queryFn,
    enabled: !!userId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> }
}

/**
 * @summary Get user by ID with role assignments
 */
export function useGetUserById<
  TData = Awaited<ReturnType<typeof getUserById>>,
  TError = ErrorType<ErrorResponse>,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserById>>, TError, TData>
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>
} {
  const queryOptions = getUserByIdQueryOptions(userId, options)

  const query = useQuery(
    { staleTime: USER_DETAIL_STALE_TIME, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>
  }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * @summary Update user profile
 */
export const useUpdateUser = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserById>>,
      TError,
      { userId: string; data: BodyType<RequestUpdateUser> },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateUserById>>,
  TError,
  { userId: string; data: BodyType<RequestUpdateUser> },
  TContext
> => {
  const mutationKey = ['updateUserAccess']

  let resolvedOptions: {
    mutation: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserById>>,
      TError,
      { userId: string; data: BodyType<RequestUpdateUser> },
      TContext
    > & { mutationKey: string[] }
    request?: SecondParameter<typeof customInstance>
  }
  if (!options) {
    resolvedOptions = { mutation: { mutationKey }, request: undefined }
  } else if (
    options.mutation &&
    'mutationKey' in options.mutation &&
    options.mutation.mutationKey
  ) {
    resolvedOptions = options as typeof resolvedOptions
  } else {
    resolvedOptions = {
      ...options,
      mutation: { ...options.mutation, mutationKey },
    }
  }

  const { mutation: mutationOptions, request: requestOptions } = resolvedOptions

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUserById>>,
    { userId: string; data: BodyType<RequestUpdateUser> }
  > = (props) => {
    const { userId, data } = props ?? {}

    return updateUserById(userId, data, requestOptions)
  }

  return useMutation({ mutationFn, ...mutationOptions }, queryClient)
}

export const getUpdateUserPermissionsMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateUserPermissions>>,
    TError,
    { userId: string; data: BodyType<RequestAssignPermission> },
    TContext
  >
  request?: SecondParameter<typeof customInstance>
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateUserPermissions>>,
  TError,
  { userId: string; data: BodyType<RequestAssignPermission> },
  TContext
> => {
  const mutationKey = ['updateUserPermissions']

  let resolvedOptions: {
    mutation: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserPermissions>>,
      TError,
      { userId: string; data: BodyType<RequestAssignPermission> },
      TContext
    > & { mutationKey: string[] }
    request?: SecondParameter<typeof customInstance>
  }
  if (!options) {
    resolvedOptions = { mutation: { mutationKey }, request: undefined }
  } else if (
    options.mutation &&
    'mutationKey' in options.mutation &&
    options.mutation.mutationKey
  ) {
    resolvedOptions = options as typeof resolvedOptions
  } else {
    resolvedOptions = {
      ...options,
      mutation: { ...options.mutation, mutationKey },
    }
  }

  const { mutation: mutationOptions, request: requestOptions } = resolvedOptions

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateUserPermissions>>,
    { userId: string; data: BodyType<RequestAssignPermission> }
  > = (props) => {
    const { userId, data } = props ?? {}

    return updateUserPermissions(userId, data, requestOptions)
  }

  return { mutationFn, ...mutationOptions }
}

/**
 * @summary Assign or edit project-scoped permissions for a user
 */
export const useUpdateUserPermissions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateUserPermissions>>,
      TError,
      { userId: string; data: BodyType<RequestAssignPermission> },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateUserPermissions>>,
  TError,
  { userId: string; data: BodyType<RequestAssignPermission> },
  TContext
> => {
  const mutationOptions = getUpdateUserPermissionsMutationOptions(options)

  return useMutation(mutationOptions, queryClient)
}
